import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTasks } from './useData';
import type { Task } from './useData';
import { useTheme } from '../../contexts/ThemeContext';
import { XIcon } from '../../components/ui/Icons';
import { cn } from '../../lib/utils';
import { formatDateTime } from '../../config/appConfig';
import { ReportExportButtons } from '../../components/reports/ReportExportButtons';
import { exportReportToExcel, exportReportToPDF } from '../../utils/reportExport';
import DataTable, { Column } from '../../components/DataTable/DataTable';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Format minutes as "Xh Ym" or "Xm". */
function formatMinutes(min: number): string {
  if (min === 0) return '0m';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Get month index (0-11) from task date. Uses taskDate or taskStartDate. */
function getTaskMonthIndex(task: Task): number | null {
  const dateStr = task.taskDate || task.taskStartDate;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.getMonth();
}

/** Get year from task date. */
function getTaskYear(task: Task): number | null {
  const dateStr = task.taskDate || task.taskStartDate;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

export interface MonthClientRow {
  monthIndex: number;
  monthLabel: string;
  clientId: number | null;
  clientName: string;
  taskCount: number;
  spentMin: number;
  invoicedMin: number;
}

const TasksMonthlyView: React.FC = () => {
  const { colors, theme } = useTheme();
  const navigate = useNavigate();
  const { tasks, isLoading, isError } = useTasks();
  const isMobile = false;
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  /** When set, show a modal listing tasks for this row (month + client). */
  const [drillDownRow, setDrillDownRow] = useState<MonthClientRow | null>(null);

  // Tasks in selected year (by taskDate or taskStartDate)
  const tasksInYear = useMemo(() => {
    return tasks.filter((t) => getTaskYear(t) === selectedYear);
  }, [tasks, selectedYear]);

  // Year options: last 6 years (same style as client-tasks-monthly report)
  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - 5 + i).sort((a, b) => a - b),
    [currentYear]
  );

  // Aggregate by month and client: monthIndex -> clientId -> { count, spentMin, invoicedMin, clientName }
  const byMonthClient = useMemo(() => {
    const map: Record<number, Record<number | string, { clientName: string; count: number; spentMin: number; invoicedMin: number }>> = {};
    for (let i = 0; i < 12; i++) map[i] = {};
    tasksInYear.forEach((t) => {
      const mi = getTaskMonthIndex(t);
      if (mi == null) return;
      const cid = t.clientId ?? 'none';
      const name = t.client?.clientName ?? (t.clientId ? `Client #${t.clientId}` : 'No client');
      if (!map[mi][cid]) {
        map[mi][cid] = { clientName: name, count: 0, spentMin: 0, invoicedMin: 0 };
      }
      map[mi][cid].count += 1;
      map[mi][cid].spentMin += t.taskSpentMin ?? 0;
      map[mi][cid].invoicedMin += t.taskInvoicedMin ?? 0;
    });
    return map;
  }, [tasksInYear]);

  // Flat rows for table: month, client, count, spent, invoiced
  const tableRows = useMemo((): MonthClientRow[] => {
    const rows: MonthClientRow[] = [];
    const monthRange = selectedMonth !== null ? [selectedMonth] : Array.from({ length: 12 }, (_, i) => i);
    monthRange.forEach((mi) => {
      const clients = byMonthClient[mi] ?? {};
      Object.entries(clients).forEach(([cid, agg]) => {
        if (agg.count === 0) return;
        rows.push({
          monthIndex: mi,
          monthLabel: MONTHS[mi],
          clientId: cid === 'none' ? null : Number(cid),
          clientName: agg.clientName,
          taskCount: agg.count,
          spentMin: agg.spentMin,
          invoicedMin: agg.invoicedMin,
        });
      });
    });
    return rows.sort((a, b) => a.monthIndex - b.monthIndex || (a.clientName || '').localeCompare(b.clientName || ''));
  }, [byMonthClient, selectedMonth]);

  /** Tasks for the drill-down row (month + client). */
  const drillDownTasks = useMemo((): Task[] => {
    if (!drillDownRow) return [];
    return tasksInYear.filter((t) => {
      const mi = getTaskMonthIndex(t);
      if (mi !== drillDownRow.monthIndex) return false;
      const cid = t.clientId ?? null;
      return cid === drillDownRow.clientId;
    });
  }, [tasksInYear, drillDownRow]);

  // Totals for selected period
  const totals = useMemo(() => {
    let count = 0;
    let spentMin = 0;
    let invoicedMin = 0;
    tableRows.forEach((r) => {
      count += r.taskCount;
      spentMin += r.spentMin;
      invoicedMin += r.invoicedMin;
    });
    return { count, spentMin, invoicedMin };
  }, [tableRows]);

  // Per-month summary for the grid (count of tasks per month)
  const byMonthCount = useMemo(() => {
    const arr = Array.from({ length: 12 }, () => 0);
    tasksInYear.forEach((t) => {
      const mi = getTaskMonthIndex(t);
      if (mi != null) arr[mi] += 1;
    });
    return arr;
  }, [tasksInYear]);

  const reportTitle = `Monthly Tasks · ${selectedYear}`;
  const fileName = `monthly-tasks-${selectedYear}`;
  const onExportExcel = useCallback(async () => {
    const headers = ['Month', 'Client', 'Tasks', 'Spent time', 'Invoiced time'];
    const rows = tableRows.map((r) => [
      r.monthLabel,
      r.clientName ?? '',
      r.taskCount,
      formatMinutes(r.spentMin),
      formatMinutes(r.invoicedMin),
    ]);
    await exportReportToExcel({ reportTitle, fileName, table: { headers, rows } });
  }, [tableRows, reportTitle, fileName]);
  const onExportPDF = useCallback(async () => {
    const headers = ['Month', 'Client', 'Tasks', 'Spent time', 'Invoiced time'];
    const rows = tableRows.map((r) => [
      r.monthLabel,
      r.clientName ?? '',
      r.taskCount,
      formatMinutes(r.spentMin),
      formatMinutes(r.invoicedMin),
    ]);
    await exportReportToPDF({ reportTitle, fileName, table: { headers, rows } });
  }, [tableRows, reportTitle, fileName]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={colors.textMuted}>Loading tasks...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={colors.textMuted}>Failed to load tasks.</div>
      </div>
    );
  }

  const monthLabel = selectedMonth !== null ? MONTHS[selectedMonth] : null;

  return (
    <div className="h-full flex flex-col">
      {/* Year on the left, export buttons on the right */}
      <div className="mb-2 flex-shrink-0 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={cn('text-xs font-medium mr-1', colors.textMuted)}>Year</span>
          <div className={cn('inline-flex rounded-lg p-1 gap-1.5 border', colors.border, colors.surface)} role="group" aria-label="Select year">
            {yearOptions.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => { setSelectedYear(y); setSelectedMonth(null); }}
                className={cn(
                  'min-w-[2.75rem] px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors',
                  selectedYear === y
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : cn(colors.text, colors.surfaceHover, 'hover:bg-gray-100 dark:hover:bg-gray-700')
                )}
                aria-pressed={selectedYear === y}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/crm_tasks')}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
              colors.border,
              colors.surfaceHover,
              'hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
            title="List View"
            aria-label="List View"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
          <ReportExportButtons onExportExcel={onExportExcel} onExportPDF={onExportPDF} disabled={tableRows.length === 0} />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        <div className={`flex-shrink-0 ${isMobile ? 'px-2' : 'px-0'} mb-4`}>
          {/*

          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4`}>
            <div className={`${colors.surface} ${colors.border} border rounded-xl p-4 sm:p-5 shadow-md ${theme === 'dark' ? 'from-gray-800' : 'from-white to-blue-50/30'} bg-gradient-to-br`}>
              <p className={`text-xs font-medium ${colors.textMuted} uppercase tracking-wide mb-1`}>
                {monthLabel ? `${monthLabel} ` : ''}{selectedYear} · Tasks
              </p>
              <p className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>{totals.count}</p>
            </div>
            <div className={`${colors.surface} ${colors.border} border rounded-xl p-4 sm:p-5 shadow-md ${theme === 'dark' ? 'from-gray-800' : 'from-white to-green-50/30'} bg-gradient-to-br`}>
              <p className={`text-xs font-medium ${colors.textMuted} uppercase tracking-wide mb-1`}>Spent time</p>
              <p className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>{formatMinutes(totals.spentMin)}</p>
            </div>
            <div className={`${colors.surface} ${colors.border} border rounded-xl p-4 sm:p-5 shadow-md ${theme === 'dark' ? 'from-gray-800' : 'from-white to-purple-50/30'} bg-gradient-to-br`}>
              <p className={`text-xs font-medium ${colors.textMuted} uppercase tracking-wide mb-1`}>Invoiced time</p>
              <p className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>{formatMinutes(totals.invoicedMin)}</p>
            </div>
          </div>
          */}

          {/* Month grid */}
          <div className={`${colors.surface} ${colors.border} border rounded-xl p-4 sm:p-6 shadow-md mb-4`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-base font-semibold ${colors.text}`}>Monthly breakdown ({selectedYear})</h3>
              {monthLabel && (
                <button
                  type="button"
                  onClick={() => setSelectedMonth(null)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${colors.textMuted} hover:${colors.text} ${colors.surfaceHover} transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  title="Show all months"
                >
                  <XIcon className="w-3.5 h-3.5" />
                  Clear month filter
                </button>
              )}
            </div>
            <div className={`grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-2`}>
              {MONTHS.map((m, i) => {
                const isSelected = selectedMonth === i;
                const count = byMonthCount[i] ?? 0;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setSelectedMonth((prev) => (prev === i ? null : i))}
                    className={`rounded-lg py-2.5 px-2 text-center transition-all ${
                      isSelected
                        ? `ring-2 ring-indigo-500 ${theme === 'dark' ? 'bg-indigo-900/40' : 'bg-indigo-50'}`
                        : `${colors.surfaceHover} hover:shadow ${count > 0 ? 'cursor-pointer' : 'opacity-50 cursor-default'}`
                    }`}
                    disabled={count === 0}
                  >
                    <p className={`text-[10px] sm:text-xs font-medium ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : colors.textMuted} mb-0.5`}>{m}</p>
                    <p className={`text-sm font-bold ${colors.text}`}>{count}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Table: Client × Month → count, spent, invoiced (using shared DataTable) */}
        <div className={`flex-1 min-h-0 flex flex-col ${colors.surface} ${colors.border} border rounded-xl shadow-md overflow-hidden`}>
          <div className={`flex-shrink-0 px-4 sm:px-6 py-4 border-b ${colors.border}`}>
            <h3 className={`text-base font-semibold ${colors.text}`}>
              {monthLabel ? `Clients in ${monthLabel} ${selectedYear}` : `Tasks by month and client · ${selectedYear}`}
            </h3>
          </div>
          <div className="flex-1 min-h-0">
            <DataTable<MonthClientRow>
              data={tableRows}
              keyExtractor={(row) => `${row.monthIndex}-${row.clientId ?? 'none'}`}
              showFooter
              stickyHeader
              emptyMessage={
                monthLabel
                  ? `No tasks in ${monthLabel} ${selectedYear}.`
                  : `No tasks with a date in ${selectedYear}.`
              }
              columns={[
                {
                  key: 'monthLabel',
                  header: 'Month',
                  render: (value) => <span className={colors.text}>{value}</span>,
                } as Column<MonthClientRow>,
                {
                  key: 'clientName',
                  header: 'Client',
                  render: (_value, row) =>
                    row.clientId != null ? (
                      <Link
                        to={`/clients/${row.clientId}`}
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        {row.clientName}
                      </Link>
                    ) : (
                      <span className={colors.text}>{row.clientName}</span>
                    ),
                } as Column<MonthClientRow>,
                {
                  key: 'taskCount',
                  header: 'Tasks',
                  align: 'right',
                  footerType: 'sum',
                  footerLabel: 'Total tasks',
                  render: (_value, row) => (
                    <button
                      type="button"
                      onClick={() => setDrillDownRow(row)}
                      className={cn(
                        'font-medium rounded px-2 py-0.5 min-w-[2rem] transition-colors',
                        row.taskCount > 0
                          ? 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                          : colors.text
                      )}
                      title="View tasks"
                    >
                      {row.taskCount}
                    </button>
                  ),
                } as Column<MonthClientRow>,
                {
                  key: 'spentMin',
                  header: 'Spent time',
                  align: 'right',
                  footerType: 'sum',
                  footerLabel: 'Total spent',
                  render: (_value, row) => (
                    <span className={colors.text}>{formatMinutes(row.spentMin)}</span>
                  ),
                } as Column<MonthClientRow>,
                {
                  key: 'invoicedMin',
                  header: 'Invoiced time',
                  align: 'right',
                  footerType: 'sum',
                  footerLabel: 'Total invoiced',
                  render: (_value, row) => (
                    <span className={colors.text}>{formatMinutes(row.invoicedMin)}</span>
                  ),
                } as Column<MonthClientRow>,
              ]}
            />
          </div>
        </div>
      </div>

      {/* Drill-down modal: tasks for selected row */}
      {drillDownRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setDrillDownRow(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tasks-modal-title"
        >
          <div
            className={cn(
              'flex flex-col max-h-[85vh] w-full max-w-2xl rounded-xl shadow-xl overflow-hidden',
              colors.surface,
              colors.border,
              'border'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('flex-shrink-0 flex items-center justify-between px-4 py-3 border-b', colors.border)}>
              <h2 id="tasks-modal-title" className={cn('text-lg font-semibold', colors.text)}>
                Tasks · {drillDownRow.monthLabel} {selectedYear} · {drillDownRow.clientName}
              </h2>
              <button
                type="button"
                onClick={() => setDrillDownRow(null)}
                className={cn('p-2 rounded-lg', colors.textMuted, colors.surfaceHover)}
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
              {drillDownTasks.length === 0 ? (
                <p className={cn('text-sm', colors.textMuted)}>No tasks.</p>
              ) : (
                drillDownTasks.map((task) => (
                  <div
                    key={task.taskId}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors',
                      colors.border,
                      colors.surfaceHover,
                      'hover:ring-1 hover:ring-indigo-500/50'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          setDrillDownRow(null);
                          navigate(`/crm_tasks/${task.taskId}`);
                        }}
                        className={cn(
                          'text-left w-full font-medium text-indigo-600 dark:text-indigo-400 hover:underline truncate block',
                          (task.taskSubject || task.taskDescription) ? colors.text : colors.textMuted
                        )}
                      >
                        {task.taskSubject || task.taskDescription || `Task #${task.taskId}`}
                      </button>
                      <div className={cn('flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs', colors.textMuted)}>
                        {task.taskStartDate && (
                          <span>Start: {formatDateTime(task.taskStartDate)}</span>
                        )}
                        {task.taskDate && (
                          <span>Task date: {formatDateTime(task.taskDate)}</span>
                        )}
                        {(task.taskSpentMin != null && task.taskSpentMin > 0) && (
                          <span>Spent: {formatMinutes(task.taskSpentMin)}</span>
                        )}
                        {task.taskInvoicedMin != null && (
                          <span>Invoiced: {formatMinutes(task.taskInvoicedMin)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <Link
                        to={`/crm_tasks/${task.taskId}`}
                        onClick={() => setDrillDownRow(null)}
                        className={cn(
                          'p-2 rounded-lg',
                          colors.surfaceHover,
                          colors.textMuted,
                          'hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors'
                        )}
                        title="View"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Link>
                      <Link
                        to={`/crm_tasks/${task.taskId}/edit`}
                        onClick={() => setDrillDownRow(null)}
                        className={cn(
                          'p-2 rounded-lg',
                          colors.surfaceHover,
                          'text-indigo-600 dark:text-indigo-400 hover:ring-1 hover:ring-indigo-500/50 transition-colors'
                        )}
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 012.828-2.828L15 14.172V9a2 2 0 00-2-2h-2z" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksMonthlyView;
